import { Decimal } from "decimal.js";
import { FC, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Form, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Message, Messages, useAlert } from "capitalrx-components-2.0/lib/general";
import { Stack } from "capitalrx-components-2.0/lib/layout";
import { Paragraph } from "capitalrx-components-2.0/lib/typography";

import { useCreateMedicarePrescriptionPaymentPlanRefundStatusMutation } from "@/App/api/medicarePrescriptionPaymentPlan";
import { formatDollars } from "@/App/utils/utils";
import { useQueryClient } from "@tanstack/react-query";

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentHistoryRefundModal";

export interface RefundModalProps {
  readonly maxRefund: Decimal;
  readonly isVisible: boolean;
  readonly onClose: () => void;
  readonly planYear?: number | null;
}

export const RefundModal: FC<RefundModalProps> = ({ isVisible, onClose, maxRefund, planYear }) => {
  const { t: translate } = useTranslation();
  const queryClient = useQueryClient();

  const form = useForm<{ refundAmount: Decimal }>();
  const showAlert = useAlert();

  const { mutateAsync: createRefund } = useCreateMedicarePrescriptionPaymentPlanRefundStatusMutation(planYear);

  const closeModal = useCallback(() => {
    form.reset();
    onClose();
  }, [form]);

  const handleSubmit = async (values: { refundAmount: Decimal }) => {
    try {
      const refundResponse = await createRefund({
        tx_amount: values.refundAmount,
        plan_year: planYear,
      });
      if (refundResponse.refund_status === "incomplete" && refundResponse.refund_pending) {
        showAlert({
          message: `${translate(`${TRANSLATION_PATH}.partialRefundMessage`)} ${formatDollars(refundResponse.refund_pending)}`,
          type: "error",
          style: "banner",
        });
      } else {
        showAlert({
          message: translate(`${TRANSLATION_PATH}.successfullyCreatedRefund`),
          type: "success",
          style: "banner",
        });
      }
    } catch (err) {
      showAlert({
        message: typeof err === "string" ? err : err.response?.data?.message || err.data?.message || translate(`${TRANSLATION_PATH}.unableToCreateRefund`),
        type: "error",
        style: "banner",
      });
    }
    queryClient.invalidateQueries({ queryKey: ["listMedicarePrescriptionPaymentPlanPaymentHistory"] }),
    closeModal();
  };

  const validateRefundAmount = useCallback(
    (value: string) => {
      const numbersAndDecimalsOnly = /^\d+(\.\d{1,2})?$/.test(value);
      if (!numbersAndDecimalsOnly) {
        return translate(`${TRANSLATION_PATH}.amountMayIncludeNumbersAndDecimal`);
      }
      const decValue = new Decimal(value);
      if (decValue.greaterThan(maxRefund)) {
        return translate(`${TRANSLATION_PATH}.amountCannotExceedRefundable`);
      } else if (decValue.lessThanOrEqualTo(0.5)) {
        return translate(`${TRANSLATION_PATH}.amountCannotBeLessThan50Cents`);
      }
      return undefined;
    },
    [maxRefund],
  );

  return (
    <Form.Modal
      form={form}
      visible={isVisible}
      title={translate(`${TRANSLATION_PATH}.requestRefund`)}
      onClose={closeModal}
      canReset={false}
      onSubmit={handleSubmit}
      submitContent={translate(`${TRANSLATION_PATH}.request`)}
    >
      <Stack>
        <Message appearance={Messages.Warning} visible={isVisible}>
          {translate(`${TRANSLATION_PATH}.yearToDateAdjustmentMessage`)}
        </Message>
        <Paragraph>
          {translate(`${TRANSLATION_PATH}.refundRequestDescription`)}
          <b> {formatDollars(maxRefund.toString())}</b>.
        </Paragraph>
        <Textbox.Field
          instructions={translate(`${TRANSLATION_PATH}.refundAmountInstructions`)}
          label={translate(`${TRANSLATION_PATH}.refundAmount`)}
          name="refundAmount"
          validator={validateRefundAmount}
          placeholder="$"
          required={true}
          testId="refund-amount-input"
        />
      </Stack>
    </Form.Modal>
  );
};
